import { Icons } from '../assets/icons';
import { DropdownOption } from '../components/Dropdown';
import { TokenInformation } from '../sdk/helpers/getTokenInformation';
import { WeiAmount } from '../components/WeiInput';
import { Duration } from '../sdk/helpers/duration';
import { RewardsPoolBase } from '../sdk/staking-v2/RewardsPoolBase';
import { BigNumber, ethers } from 'ethers';

export const accuracy = ethers.utils.parseEther('1');
export const explorerChainId = {
  1: 'etherscan.io',
  19: 'songbird-explorer.flare.network',
  56: 'bscscan.com',
  97: 'testnet.bscscan.com',
  137: 'polygonscan.com',
  246: 'explorer.energyweb.org',
  1284: 'moonscan.io',
  43113: 'cchain.explorer.avax-test.network',
  43114: 'cchain.explorer.avax.network',
  80001: 'mumbai.polygonscan.com',
  11155111: 'sepolia.etherscan.io',
};

type ExplorerChainId = keyof typeof explorerChainId;

export const getExplorerAddressByChainId = (chainId: string | undefined): string =>
  `https://${explorerChainId[(chainId as unknown) as ExplorerChainId] || 'etherscan.io'}/address/`;

export function getNetworkAndProtocol(id: string): [string, string] {
  switch (id) {
    case '97':
    case '56':
      return ['bsc', 'pancakeswap'];
    case '137':
      return ['polygon', 'quickswap'];
    case '43114':
      return ['avalanche', 'pangolin'];
    case '43113':
      return ['avalanche', 'pangolin'];
    case '1284':
      return ['moonbeam', 'solarflare'];
    case '246':
      return ['ewc', 'alliancedex'];
    case '19':
      return ['songbird', 'pangolin'];
    default:
      return ['eth', 'uniswap'];
  }
}

export function getProtocolOptions(network: string): DropdownOption[] {
  if (network === 'bsc') {
    return [
      {
        value: 'pancakeswap',
        icon: Icons['pancakeswap'],
        name: 'Pancakeswap',
      },
    ];
  }

  if (network === 'avalanche') {
    return [
      {
        value: 'pangolin',
        icon: Icons['pangolin'],
        name: 'Pangolin',
      },
      {
        value: 'traderjoe',
        icon: Icons['traderjoe'],
        name: 'Trader joe',
      },
      {
        value: 'uniswapv3',
        icon: Icons['uniswap'],
        name: 'Uniswap V3',
      },
    ];
  }

  if (network === 'polygon') {
    return [
      {
        value: 'quickswap',
        icon: Icons['quickswap'],
        name: 'Quickswap',
      },
    ];
  }

  if (network === 'moonbeam') {
    return [
      {
        value: 'solarflare',
        icon: Icons['solarflare'],
        name: 'Solarflare',
      },
    ];
  }

  if (network === 'songbird') {
    return [
      {
        value: 'pangolin',
        icon: Icons['pangolin'],
        name: 'Pangolin',
      },
    ];
  }

  if (network === 'ewc') {
    return [
      {
        value: 'alliancedex',
        icon: Icons['allianceblock'],
        name: 'AllianceBlock DEX',
      },
    ];
  }

  return [
    {
      value: 'uniswap',
      icon: Icons['uniswap'],
      name: 'Uniswap',
    },
    {
      value: 'balancer',
      icon: Icons['balancer'],
      name: 'Balancer',
    },
  ];
}

export function getVersionDropdownOptions(): DropdownOption[] {
  return [
    {
      value: PoolVersions.v1,
      icon: Icons.pool,
      name: 'version 1.0',
    },
    {
      value: PoolVersions.v2,
      icon: Icons.pool,
      name: 'version 2.0',
    },
    {
      value: PoolVersions.v3,
      icon: Icons.pool,
      name: 'version 3.0',
    },
    {
      value: PoolVersions.v4,
      icon: Icons.pool,
      name: 'version 4.0',
    },
  ].filter((option) => (process.env.REACT_APP_ALLOWED_VERSIONS || '1.0').split(',').includes(option.value));
}

export const minimumPoolScheduleTime = new Date().toISOString().slice(0, -1).slice(0, 16);

export const PoolVersions: {
  [key: string]: PoolVersion;
} = {
  v1: '1.0',
  v2: '2.0',
  v3: '3.0',
  v4: '4.0',
};

export type PoolVersion = '1.0' | '2.0' | '3.0' | '4.0';
export type CardTypes = 'lmc' | 'staking' | 'infiniteStaking';

export interface CreatePoolState {
  step: number;
  version: PoolVersion;
  name: string;
  campaignMessage: string;
  protocol: string;
  stakingAddress: string;
  userStakingLimit: WeiAmount;
  stakingLimit: WeiAmount;
  rewards: { readOnly: boolean; info: TokenInformation; reward: WeiAmount }[];
  throttleRoundDuration: Duration;
  throttleRoundCap: WeiAmount;
  pool: RewardsPoolBase;
}

export interface OnCreatePoolProps {
  protocol: string;
  tokenAddress: string;
  rewards: { readOnly: boolean; info: TokenInformation; reward: WeiAmount }[];
  userStakingLimit: WeiAmount;
  stakingLimit: WeiAmount;
  throttleRoundDuration: Duration;
  throttleRoundCap: WeiAmount;
  tokenInformation: TokenInformation[];
  name: string;
  campaignMessage: string;
  tier: string;
  locked: boolean;
}

export enum PoolType {
  LIQUIDITY_MINING = 1,
  STAKING = 2,
  INFINITE_STAKING = 3,
}

export interface CreatePoolProps {
  title: string;
  subTitle: string;
  tokenTitle: string;
  tokenLabel: string;
  showProtocol?: boolean;
  showTier?: boolean;
  showThrottleRound?: boolean;
  onCreatePool: (props: OnCreatePoolProps) => Promise<{ pool: RewardsPoolBase; info: any }>;
  newForm?: boolean;
  rewardsLimit: number;
  singleSidedToken?: boolean;
  type: number;
  state?: CreatePoolState;
}

export interface Reward {
  readOnly: boolean;
  info: TokenInformation;
  reward: WeiAmount;
}

export interface LockingPeriod {
  duration: Duration;
  bonus: number;
  increaseDuration: Duration;
  noLock: boolean;
}

export interface LMReward {
  symbol: string;
  address: string;
  excessRewards?: BigNumber;
  hasExcessRewards?: boolean;
  rewardPerSecond?: BigNumber;
  rewardPerBlock?: BigNumber;
  totalRewards: BigNumber;
  weeklyRewards: BigNumber;
  decimals: number;
  remainingRewardAmount: BigNumber;
}
